import React, { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheck, FormInput } from "go-form";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomValidationConfig, CustomValidationConstraint, CustomValidationError } from "go-form/services/types";
import { PaymentMethodApi } from "../../../../../../../../../services/Api/Organization/types";

interface Props {
	form: UseFormReturn<PaymentMethodApi>;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<PaymentMethodApi>[]>>;
}

const PaymentMethodBasicInfoForm = ({ form, setErrors }: Props) => {
	const { t } = useTranslation();
	const {
		register,
		formState: { errors },
		control,
		watch,
	} = form;

	const paymentOutflowTypes = [
		{ value: "WZ", label: `${t("enums.payment_methods.payment_outflow_types.WZ")}` },
		{ value: "RW", label: `${t("enums.payment_methods.payment_outflow_types.RW")}` },
	];

	const paymentReceiptTypes = [
		{ value: "CASH", label: `${t("enums.payment_methods.payment_receipt_types.CASH")}` },
		{ value: "CARD", label: `${t("enums.payment_methods.payment_receipt_types.CARD")}` },
		{ value: "CHECK", label: `${t("enums.payment_methods.payment_receipt_types.CHECK")}` },
		{ value: "BOND", label: `${t("enums.payment_methods.payment_receipt_types.BOND")}` },
		{ value: "CREDIT", label: `${t("enums.payment_methods.payment_receipt_types.CREDIT")}` },
		{ value: "OTHER", label: `${t("enums.payment_methods.payment_receipt_types.OTHER")}` },
		{ value: "VOUCHER", label: `${t("enums.payment_methods.payment_receipt_types.VOUCHER")}` },
		{ value: "CLIENT_ACCOUNT", label: `${t("enums.payment_methods.payment_receipt_types.CLIENT_ACCOUNT")}` },
		{ value: "BANK_TRANSFER", label: `${t("enums.payment_methods.payment_receipt_types.BANK_TRANSFER")}` },
	];

	const nameValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	return (
		<fieldset className="form-group no-gutters">
			<h5>{t("common.word.basic_data", { ns: "lib" })}</h5>
			<FormInput
				label={t("common.word.name", { ns: "lib" })}
				register={register}
				name="name"
				errors={errors}
				customValidationConfig={nameValidationConfig}
			/>
			<FormSelectGroup
				label={t("modules.payment_method.field.outflow_type.title")}
				name="outflow_type"
				errors={errors}
				options={paymentOutflowTypes}
				control={control}
				data-testid="outflow_type"
				isClearable={false}
			/>
			<FormSelectGroup
				label={t("modules.payment_method.field.receipt_type.title")}
				name="receipt_type"
				errors={errors}
				options={paymentReceiptTypes}
				control={control}
				data-testid="receipt_type"
				isClearable={false}
			/>
			<FormCheck
				type="switch"
				register={register}
				label={t("modules.payment_method.field.auto_closed.title")}
				name="auto_closed"
				errors={errors}
				help={t("modules.payment_method.field.auto_closed.help_text.title")}
			/>
			<FormCheck
				type="switch"
				register={register}
				label={t("modules.payment_method.field.print_receipt.title")}
				name="print_receipt"
				errors={errors}
			/>
			<FormCheck
				type="switch"
				register={register}
				label={t("modules.payment_method.field.open_cash_drawer.title")}
				name="open_cash_drawer"
				errors={errors}
			/>
		</fieldset>
	);
};

export default PaymentMethodBasicInfoForm;
